import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Custom animations
import { initAllAnimations, initCounterAnimations, initParallaxEffect } from './utils/animations';

// Components
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import DecorativeShapes from './components/DecorativeShapes/DecorativeShapes';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';

// Pages
import Home from './pages/Home/Home';
import About from './pages/About/About';
import Services from './pages/Services/Services';
import Gallery from './pages/Gallery/Gallery';
import Careers from './pages/Careers/Careers';
import Testimonials from './pages/Testimonials/Testimonials';
import Blog from './pages/Blog/Blog';
import BlogDetail from './pages/Blog/BlogDetail';
import Contact from './pages/Contact/Contact';
import Admin from './pages/Admin/Admin';

// Styles
import './App.css';
import './styles/mobile-font-override.css';

function App() {
  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 400,
      easing: 'ease-out-cubic',
      once: true,
      offset: 100,
      delay: 0,
    });

    // Refresh AOS on route changes
    AOS.refresh();

    // Initialize custom animations
    initAllAnimations();
    initCounterAnimations();
    initParallaxEffect();

    // Smooth scrolling for anchor links
    const handleSmoothScroll = (e) => {
      const target = e.target.getAttribute('href');
      if (target && target.startsWith('#')) {
        e.preventDefault();
        const element = document.querySelector(target);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    };

    // Add smooth scroll event listeners
    document.addEventListener('click', handleSmoothScroll);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleSmoothScroll);
    };
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <Helmet>
          {/* Google Fonts - Exact Solafy Typography */}
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
          <link 
            href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Poppins:wght@300;400;500;600;700;800;900&display=swap" 
            rel="stylesheet" 
          />
          
          {/* Font Awesome */}
          <link 
            rel="stylesheet" 
            href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"
            integrity="sha512-iecdLmaskl7CVkqkXNQ/ZH/XLlvWZOJyj7Yy7tcenmpD1ypASozpmT/E0iPtmFIB46ZmdtAc9eNBvH0H/ZpiBw=="
            crossorigin="anonymous"
            referrerpolicy="no-referrer"
          />

          {/* Meta Tags */}
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta name="theme-color" content="#22c55e" />
          <meta name="description" content="Global Solar Energy - Leading solar energy solutions provider in Gujarat since 2015. We design, manufacture, and install high-efficiency solar systems for residential and commercial use." />
          <meta name="keywords" content="solar energy, solar panels, solar installation, renewable energy, Gujarat solar, Global Solar Energy, residential solar, commercial solar, MNRE approved, make in india" />
          <meta name="author" content="Global Solar Energy" />
          
          {/* Open Graph */}
          <meta property="og:title" content="Global Solar Energy - Think Solar for Better Future" />
          <meta property="og:description" content="Leading solar energy solutions provider in Gujarat since 2015. Premium Australian Technology Smart Inverters with Make in India Solar Panels." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://globalsolarenergy.in" />
          <meta property="og:image" content="https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" />
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Global Solar Energy - Think Solar for Better Future" />
          <meta name="twitter:description" content="Leading solar energy solutions provider in Gujarat since 2015. Premium Australian Technology Smart Inverters with Make in India Solar Panels." />
          <meta name="twitter:image" content="https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" />

          {/* Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Global Solar Energy",
              "description": "Leading solar energy solutions provider in Gujarat since 2015",
              "url": "https://globalsolarenergy.in",
              "logo": "https://globalsolarenergy.in/logo.png",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+91-72028-58358",
                "contactType": "customer service",
                "areaServed": "IN",
                "availableLanguage": ["English", "Hindi", "Gujarati"]
              },
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Amreli",
                "addressRegion": "Gujarat",
                "addressCountry": "IN"
              },
              "sameAs": [
                "https://www.facebook.com/globalsolarenergy",
                "https://www.instagram.com/globalsolarenergy",
                "https://www.linkedin.com/company/globalsolarenergy"
              ],
              "foundingDate": "2015",
              "numberOfEmployees": "10-50",
              "industry": "Solar Energy",
              "services": [
                "Residential Solar Installation",
                "Commercial Solar Installation", 
                "Solar Maintenance",
                "Government Subsidy Assistance"
              ]
            })}
          </script>
        </Helmet>

        <Routes>
          {/* Admin Route - Standalone without navbar/footer */}
          <Route path="/admin" element={<Admin />} />
          
          {/* Regular Routes with navbar/footer */}
          <Route path="/*" element={
            <>
              {/* Decorative Background Shapes */}
              <DecorativeShapes />
              
              {/* Navigation */}
              <Navbar />
              
              {/* Main Content */}
              <main className="main-content">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/testimonials" element={<Testimonials />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogDetail />} />
                  <Route path="/contact" element={<Contact />} />
                </Routes>
              </main>
              
              {/* Footer */}
              <Footer />
            </>
          } />
        </Routes>


        {/* Loading Overlay */}
        <LoadingOverlay />
      </div>
    </Router>
  );
}


// Loading Overlay Component
const LoadingOverlay = () => {
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  if (!isLoading) return null;

  return (
    <div className="loading-overlay">
      <div className="loading-content">
        <div className="loading-image">
          <img src="/images/Reload_image.png" alt="Loading" />
        </div>
        <p className="loading-text">Loading Solar Solutions...</p>
      </div>
    </div>
  );
};

export default App;